import React, { useEffect, useState } from 'react';
import { Ban, Copy, Radio } from 'lucide-react';
import { driverRouteService } from '@services/driverRouteService';
import { formatDateTimeBR } from '@pages/financial/financialUtils';
import type { OrderDeliveryTracking } from '@/types/driverRoute';

const LINK_STATUS: Record<string, string> = { A: 'em rota agora', D: 'entregue', S: 'suspensa' };

/**
 * Rastreamento ao vivo e entregas não realizadas do pedido (RN-ROTA-45 a 49): mostra o link
 * público que a cliente recebeu e cada suspensão com o motivo, para o atendimento. Não renderiza
 * nada quando o pedido nunca teve link nem suspensão.
 */
export const OrderDeliveryTrackingCard: React.FC<{ orderId: number }> = ({ orderId }) => {
  const [data, setData] = useState<OrderDeliveryTracking | null>(null);
  const [copied, setCopied] = useState<number | null>(null);

  useEffect(() => {
    let alive = true;
    driverRouteService
      .getOrderDeliveryTracking(orderId)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [orderId]);

  const links = data?.links ?? [];
  const suspended = data?.suspended_stops ?? [];
  if (links.length === 0 && suspended.length === 0) return null;

  const copy = async (id: number, url?: string) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(id);
    } catch {
      setCopied(null);
    }
  };

  return (
    <div className="pt-2 space-y-2">
      {links.map((l) => (
        <div key={l.delivery_tracking_link_id} className="text-xs text-slate-600 flex items-start gap-1.5">
          <Radio size={12} className="mt-0.5 flex-shrink-0 text-sky-600" />
          <div className="min-w-0">
            <span className="font-medium text-slate-700">Link de rastreamento</span>{' '}
            ({LINK_STATUS[l.status] ?? l.status}) — rota #{l.driver_route_id}, {formatDateTimeBR(l.created_at ?? '')}
            {l.in_route_whatsapp_sent_at ? ', enviado por WhatsApp' : ', WhatsApp não enviado'}
            {l.url && (
              <span className="flex items-center gap-2 mt-0.5">
                <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-sky-700 hover:underline break-all">
                  {l.url}
                </a>
                <button type="button" onClick={() => copy(l.delivery_tracking_link_id, l.url)} className="text-slate-500 hover:text-slate-700" title="Copiar link">
                  <Copy size={12} />
                </button>
                {copied === l.delivery_tracking_link_id && <span className="text-emerald-600">copiado</span>}
              </span>
            )}
          </div>
        </div>
      ))}
      {suspended.map((s) => (
        <div key={s.driver_route_stop_id} className="text-xs text-slate-600 flex items-start gap-1.5">
          <Ban size={12} className="mt-0.5 flex-shrink-0 text-red-600" />
          <span>
            <span className="font-medium text-slate-700">Entrega não realizada</span> na rota #{s.driver_route_id} em{' '}
            {formatDateTimeBR(s.suspended_at ?? '')}: {s.suspend_reason || 'sem motivo'}
            {s.suspended_by ? ` (${s.suspended_by})` : ''}
          </span>
        </div>
      ))}
    </div>
  );
};
